import React, { useState } from 'react';
import { X, Globe, Github, Tag, Share2, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Project Details Modal Component
 * 
 * Displays detailed information about a selected project in a modal overlay.
 * Handles closing on backdrop click or close button.
 * Supports image carousel and external links.
 */
const ProjectDetailsModal = ({ project, onClose }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    if (!project) return null;

    const hasImages = project.images && project.images.length > 0;

    const nextImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev === project.images.length - 1 ? 0 : prev + 1));
    };

    const prevImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev === 0 ? project.images.length - 1 : prev - 1));
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            ></div>

            {/* Modal Content */}
            <div className="relative w-full max-w-4xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-300">

                {/* Header Section: Image/Gradient & Close Button */}
                <div className="h-56 sm:h-72 bg-gray-100 dark:bg-slate-800 relative group overflow-hidden shrink-0">
                    <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-slate-900 via-transparent to-transparent z-10"></div>

                    {hasImages ? (
                        <>
                            <img 
                                src={project.images[currentImageIndex]} 
                                alt={`${project.title} screenshot ${currentImageIndex + 1}`} 
                                className="w-full h-full object-cover"
                            />
                            {project.images.length > 1 && (
                                <div className="absolute inset-0 z-20 flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={prevImage} className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-md transition-colors">
                                        <ChevronLeft size={24} />
                                    </button>
                                    <button onClick={nextImage} className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-md transition-colors">
                                        <ChevronRight size={24} />
                                    </button>
                                </div>
                            )}
                            {project.images.length > 1 && (
                                <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center gap-2">
                                    {project.images.map((_, idx) => (
                                        <div 
                                            key={idx} 
                                            className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex ? 'bg-purple-500 w-4' : 'bg-white/50'}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-100 dark:from-purple-900/50 to-indigo-100 dark:to-indigo-900/50 flex items-center justify-center">
                            {/* Fallback pattern if no image */}
                            <Globe size={64} className="text-slate-300 dark:text-white/10" />
                        </div>
                    )}

                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-30 p-2 bg-white/50 dark:bg-black/20 hover:bg-white/80 dark:hover:bg-black/40 text-slate-900 dark:text-white rounded-full backdrop-blur-md transition-colors shadow-sm"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Modal Body: Content Scrollable Area */}
                <div className="flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar relative z-20 -mt-6">
                    <div className="flex flex-col md:flex-row gap-8">

                        {/* Left: Main Info */}
                        <div className="flex-1 space-y-6">
                            <div>
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/20 shadow-sm dark:shadow-none">
                                        {project.category}
                                    </span>
                                    <span className={`px-3 py-1 text-xs font-semibold rounded-full border shadow-sm dark:shadow-none ${project.status === 'Completed'
                                        ? 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/20'
                                        : 'bg-yellow-100 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/20'
                                        }`}>
                                        {project.status}
                                    </span>
                                </div>
                                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">{project.title}</h2>
                                <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed">
                                    {project.description}
                                </p>
                            </div>

                            <div>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <Tag size={16} className="text-purple-600 dark:text-purple-400" /> Key Features
                                </h3>
                                <ul className="grid sm:grid-cols-2 gap-3">
                                    {project.features && project.features.map((feature, i) => (
                                        <li key={i} className="flex items-start gap-2 text-slate-600 dark:text-slate-400 text-sm">
                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0"></span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Right: Sidebar */}
                        <div className="w-full md:w-80 space-y-6 shrink-0">
                            <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-6 border border-gray-200 dark:border-white/5 space-y-6 shadow-sm dark:shadow-none">
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Technologies</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {project.technologies.map(tech => (
                                            <span key={tech} className="px-2.5 py-1 rounded-md bg-white dark:bg-white/5 text-slate-600 dark:text-slate-300 text-xs border border-gray-200 dark:border-white/5 hover:border-purple-300 dark:hover:border-purple-500/30 transition-colors cursor-default shadow-sm dark:shadow-none">
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="pt-6 border-t border-gray-200 dark:border-white/5 flex flex-col gap-3">
                                    {project.website && (
                                        <a
                                            href={project.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full py-3 bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-500 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors shadow-md"
                                        >
                                            <ExternalLink size={18} />
                                            Visit Website
                                        </a>
                                    )}
                                    {project.github && (
                                        <a
                                            href={project.github}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`w-full py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                                                project.website 
                                                ? 'bg-white dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 text-slate-700 dark:text-white border border-gray-200 dark:border-transparent shadow-sm' 
                                                : 'bg-slate-900 hover:bg-slate-800 dark:bg-purple-600 dark:hover:bg-purple-500 text-white shadow-md'
                                            }`}
                                        >
                                            <Github size={18} />
                                            View Source
                                        </a>
                                    )}
                                    <button className="w-full py-3 bg-white dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 text-slate-700 dark:text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors border border-gray-200 dark:border-transparent shadow-sm">
                                        <Share2 size={18} />
                                        Share Project
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectDetailsModal;
